import type { ProspectingResult } from 'App/resources/Model/ProspectingFilter';
import { RCDashboardWidget } from './ReportDashboard';

import Client from 'App/resources/Model/Client';
import Order from 'App/resources/Model/Order';
import ResourceTyped from './ResourceTyped';

export type WidgetResponse = {
	type: RCDashboardWidget['type'];
	filters: RCDashboardWidget['filters'];
	groupBy: RCDashboardWidget['groupBy'];
};

export type GetReportAction = {
	type: 'getReport';
	data: WidgetResponse & {
		timestamp: number;
	};
};

export type GetProspectingAction = {
	type: 'getProspecting';
	data: ProspectingResult;
};

type OrderSearch = {
	entity: 'Order';
	data: Order[];
};

type ClientSearch = {
	entity: 'Client';
	data: Client[];
};

export type SearchAction = (OrderSearch | ClientSearch) & {
	type: 'search';
	filters: { a: string; c: string; v: string | string[] }[];
};

export type ChatAction = SearchAction;

export type ChatSession = {
	id: string;
	name: string;
	createdAt: number;
	updatedAt: number;
	unreadCount?: number;
	nameManuallySet?: boolean;
};

export type AIChatResponse = {
	cancelled?: boolean;
	message: string;
	action?: ChatAction;
	streamed?: boolean;
	chatId?: string;
	langsmithRunId?: string;
};

class AIChatResource extends ResourceTyped<any> {
	constructor() {
		super('aiChat');
	}

	chat(params: { message: string; chatId?: string }): Promise<AIChatResponse> {
		return super._postRequest('', params).then(d => d.data);
	}

	listChats(): Promise<{ chats: ChatSession[] }> {
		return super._getRequest('listChats', {}).then(d => d.data);
	}

	createChat(): Promise<{ chatId: string; name: string; createdAt: number }> {
		return super._postRequest('createChat', {}).then(d => d.data);
	}

	getChat(
		chatId: string
	): Promise<{ messages: any[]; chatId: string; name: string; createdAt: number; unreadCount?: number }> {
		return super._getRequest(`chat/${chatId}`, {}).then(d => d.data);
	}

	markChatAsRead(chatId: string): Promise<{ message: string }> {
		return super._postRequest(`chat/${chatId}/markAsRead`, {}).then(d => d.data);
	}

	incrementUnreadCount(chatId: string): Promise<{ message: string }> {
		return super._postRequest(`chat/${chatId}/incrementUnread`, {}).then(d => d.data);
	}

	deleteChat(chatId: string): Promise<{ message: string }> {
		return super._deleteRequest(`chat/${chatId}`).then(d => d.data);
	}

	renameChat(chatId: string, name: string): Promise<{ chatId: string; name: string }> {
		return super._putRequest(`chat/${chatId}/rename`, { name }).then(d => d.data);
	}

	clearMessages(chatId: string): Promise<void> {
		const params = { chatId };
		return super._getRequest('clearMessages', params).then(d => d.data);
	}
}

const resource = new AIChatResource();
export default resource;
